import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MessageCircle, Camera, CreditCard, Shield, Book, ChevronRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useSafeBack } from '@/lib/hooks/use-safe-back';
import { useTheme } from '@/lib/contexts/theme-context';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useTranslation } from 'react-i18next';

export default function HelpPage() {
    const { t } = useTranslation('help');
    const navigate = useNavigate();
    const { goBack } = useSafeBack();
    const { theme } = useTheme();
    const [expandedCategory, setExpandedCategory] = useState(null);
    const [expandedQuestion, setExpandedQuestion] = useState(null);
    const [legalLinks, setLegalLinks] = useState({ cgu_url: null, privacy_policy_url: null });

    useEffect(() => {
        horodatyApi.adminParameters
            .getLegalLinks()
            .then((res) => {
                const data = res.data?.data;
                if (data) setLegalLinks(data);
            })
            .catch(() => {});
    }, []);

    const faqCategories = [
        {
            id: 'photos',
            icon: Camera,
            title: t('prendreDesPhotos'),
            items: [
                {
                    question: t('help:commentPrendreUnePhotoHorodatee'),
                    answer: t('help:appuyezSurLeBoutonCentralEnBasDe'),
                },
                {
                    question: t('help:commentFonctionnentLesMetadonnees'),
                    answer: t('help:chaquePhotoEstAutomatiquementEnrichieAvecLaDate'),
                },
                {
                    question: t('help:puisJeModifierUnePhotoApresLAvoir'),
                    answer: t('help:nonPourGarantirLIntegriteDeLaPreuve'),
                },
            ],
        },
        {
            id: 'tokens',
            icon: CreditCard,
            title: t('jetonsEtPaiements'),
            items: [
                {
                    question: t('help:commentFonctionnentLesJetons'),
                    answer: t('help:1JetonPermetDePrendreUnePhotoHorodatee'),
                },
                {
                    question: t('help:quelsSontLesMoyensDePaiementAcceptes'),
                    answer: t('help:nousAcceptonsLesCartesBancairesVisaMastercardEt'),
                },
                {
                    question: t('help:lesJetonsExpirentIls'),
                    answer: t('help:nonVosJetonsNOntPasDeDate'),
                },
            ],
        },
        {
            id: 'security',
            icon: Shield,
            title: t('securiteEtConfidentialite'),
            items: [
                {
                    question: t('help:mesPhotosSontEllesSecurisees'),
                    answer: t('help:ouiToutesVosPhotosSontChiffreesEtStockees'),
                },
                {
                    question: t('help:quiPeutVoirMesPhotos'),
                    answer: t('help:seulVousAvezAccesAVosPhotosNous'),
                },
                {
                    question: t('help:commentSupprimerMonCompte'),
                    answer: t('help:contactezNotreSupportPourDemanderLaSuppressionDe'),
                },
            ],
        },
        {
            id: 'usage',
            icon: Book,
            title: t('utilisationEtCasDUsage'),
            items: [
                {
                    question: t('help:dansQuelsCasUtiliserHorodaty'),
                    answer: t('help:locationsPreuvesDAssuranceEtatsDesLieuxEnvoi'),
                },
                {
                    question: t('help:lesPhotosOntEllesUneValeurLegale'),
                    answer: t('help:lesPhotosHorodateesPeuventServirDePreuvesDans'),
                },
                {
                    question: t('help:puisJePartagerMesPhotos'),
                    answer: t('help:ouiVousPouvezExporterEtPartagerVosPhotos'),
                },
            ],
        },
    ];

    const resources = [
        {
            id: 'terms',
            title: t('conditionsDUtilisation'),
            url: legalLinks.cgu_url || theme.links?.legalNotice,
        },
        {
            id: 'privacy',
            title: t('politiqueDeConfidentialite'),
            url: legalLinks.privacy_policy_url || theme.links?.privacyPolicy,
        },
    ];

    return (
        <div className="py-8 px-8 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <Button
                    variant="plain"
                    color="neutral"
                    size="auto"
                    className="bg-card rounded-full h-[45px] w-[45px]"
                    onClick={goBack}
                >
                    <ArrowLeft className="h-6 w-6 text-foreground" />
                </Button>
                <h1 className="text-2xl text-foreground">{t('centreDAide')}</h1>
                <div className="w-[45px]"></div>
            </div>

            {/* Contact Support Card */}
            <div className="bg-secondary rounded-2xl p-5 mb-[30px]">
                <div className="flex items-start gap-[15px]">
                    <div className="bg-secondary-foreground rounded-full h-10 w-10 flex items-center justify-center shrink-0">
                        <MessageCircle className="h-5 w-5 text-secondary" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-base font-medium text-secondary-foreground mb-[5px]">{t('besoinDAide')}</h3>
                        <p className="text-xs text-secondary-foreground/90 mb-3">
                            {t('notreEquipeEstDisponibleDuLundiAuVendredi')}
                        </p>
                        <Button
                            variant="solid"
                            color="neutral"
                            className="bg-secondary-foreground text-secondary hover:bg-secondary-foreground/90 rounded-lg px-5 py-2.5 h-auto"
                            onClick={() => {
                                window.location.href = `mailto:${theme.links?.supportEmail || 'support@horodaty.com'}`;
                            }}
                        >
                            {t('contacterLeSupport')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* FAQ Categories */}
            <div className="mb-[30px]">
                <h2 className="text-lg font-medium text-foreground mb-[15px]">{t('questionsFrequentes')}</h2>
                <div className="space-y-[15px]">
                    {faqCategories.map((category, categoryIndex) => {
                        const Icon = category.icon;
                        const isExpanded = expandedCategory === categoryIndex;

                        return (
                            <div key={category.id} className="bg-card rounded-2xl overflow-hidden border border-border">
                                {/* Category Header */}
                                <Button
                                    variant="plain"
                                    color="neutral"
                                    size="xxl"
                                    className="w-full p-5 flex items-center gap-[15px] hover:bg-surface-hover transition-colors rounded-none h-auto justify-start"
                                    onClick={() => setExpandedCategory(isExpanded ? null : categoryIndex)}
                                >
                                    <div className="bg-surface rounded-full h-10 w-10 flex items-center justify-center shrink-0">
                                        <Icon className="h-5 w-5 text-foreground" />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <p className="text-base text-foreground">{category.title}</p>
                                    </div>
                                    <ChevronRight
                                        className={`h-5 w-5 text-muted-foreground transition-transform ${
                                            isExpanded ? 'rotate-90' : ''
                                        }`}
                                    />
                                </Button>

                                {/* Questions */}
                                {isExpanded && (
                                    <div className="border-t border-border">
                                        {category.items.map((item, itemIndex) => {
                                            const isQuestionExpanded = expandedQuestion === item.question;

                                            return (
                                                <div key={itemIndex} className="border-b border-border last:border-b-0">
                                                    <Button
                                                        variant="plain"
                                                        color="neutral"
                                                        size="xxl"
                                                        onClick={() =>
                                                            setExpandedQuestion(
                                                                isQuestionExpanded ? null : item.question,
                                                            )
                                                        }
                                                        className="w-full h-auto p-[15px] text-left justify-start hover:bg-surface-hover transition-colors flex-col items-start rounded-none"
                                                    >
                                                        <p className="text-sm text-foreground mb-[5px]">
                                                            {item.question}
                                                        </p>
                                                        {isQuestionExpanded && (
                                                            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                                                                {item.answer}
                                                            </p>
                                                        )}
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Resources */}
            <div>
                <h2 className="text-lg font-medium text-foreground mb-[15px]">{t('ressources')}</h2>
                <div className="space-y-[10px]">
                    {resources.map((resource) => (
                        <Button
                            key={resource.id}
                            variant="plain"
                            color="neutral"
                            size="xxl"
                            className="bg-card rounded-xl w-full p-[15px] flex items-center justify-between hover:bg-surface-hover transition-colors h-auto"
                            onClick={() => resource.url && window.open(resource.url, '_blank', 'noopener,noreferrer')}
                            disabled={!resource.url}
                        >
                            <span className="text-sm text-foreground">{resource.title}</span>
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </Button>
                    ))}
                </div>
            </div>
        </div>
    );
}
